import { FormModal, SubmitProps } from '@/components/form-modal';
import { FormComboBox } from '@/components/forms/form-combo-box';
import { FormInputField } from '@/components/forms/form-input-field';
import { FormTextArea } from '@/components/forms/form-text-area';
import { useToast } from '@/components/ui/hooks/use-toast';
import { buildNameLikeSelectOptions, comboBoxOptionsOf, emptySelectOption } from '@/components/ui/multi-select-utils';
import {
  announcementFormSchema,
  AnnouncementFormSchema,
  announcementPlacementOptions,
  announcementTypeOptions,
} from '@/lib/announcement-utils';
import { createAnnouncement, getAnnouncements, updateAnnouncement } from '@/lib/api-calls';
import { airServerErrorFrom } from '@/lib/error-handling';
import {
  Announcement,
  AnnouncementPlacement,
  AnnouncementPlacementLabelMap,
  AnnouncementType,
  AnnouncementTypeLabelMap,
  UpdateAnnouncementOptions,
} from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode } from '@tanstack/react-router';

export interface AnnouncementFormModalProps {
  trigger: ReactNode;
  from?: Announcement;
}

export const AnnouncementFormModal = ({ trigger, from }: AnnouncementFormModalProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { mutateAsync: mutateAsyncCreate } = useMutation({
    mutationFn: createAnnouncement,
  });

  const { mutateAsync: mutateAsyncUpdate } = useMutation({
    mutationFn: updateAnnouncement,
  });

  const onSubmit = async ({ formData, closeModal }: SubmitProps<AnnouncementFormSchema>) => {
    try {
      const { title, content, placement, type } = formData;
      const data: UpdateAnnouncementOptions = {
        title,
        content,
        placement: placement.value as AnnouncementPlacement,
        type: type.value as AnnouncementType,
      };

      if (from && from.id) {
        await mutateAsyncUpdate({ announcementId: from.id, data });
      } else {
        await mutateAsyncCreate(data);
      }

      await queryClient.invalidateQueries(getAnnouncements());

      toast({
        description: `Announcement ${from ? 'updated' : 'created'} successfully`,
      });

      closeModal();
    } catch (e: unknown) {
      toast({
        description:
          airServerErrorFrom(e)?.userFacingMessage ??
          (e instanceof Error
            ? e.message
            : `Unable to ${from ? 'update' : 'create'} announcement. Please try again or contact Air Reading support.`),
      });
    }
  };

  const defaultValues = {
    title: from?.title || '',
    content: from?.content || '',
    placement: from?.placement
      ? buildNameLikeSelectOptions({ id: from.placement, name: AnnouncementPlacementLabelMap[from.placement] })
      : emptySelectOption,
    type: from?.type
      ? buildNameLikeSelectOptions({ id: from.type, name: AnnouncementTypeLabelMap[from.type] })
      : emptySelectOption,
  };

  return (
    <FormModal
      modalTitle={from ? 'Edit Announcement' : 'Add Announcement'}
      formSchema={announcementFormSchema}
      onSubmit={onSubmit}
      trigger={trigger}
      defaultValues={defaultValues}
    >
      {({ form }) => {
        return (
          <div className="mt-4">
            <FormInputField form={form} name="title" label="Title" />
            <FormTextArea
              form={form}
              name="content"
              label="Content"
              textAreaProps={{
                className: 'h-8',
                required: true,
              }}
            />
            <FormComboBox
              form={form}
              label="Placement"
              name="placement"
              placeholder="Select placement"
              {...comboBoxOptionsOf(announcementPlacementOptions)}
              required
            />
            <FormComboBox
              form={form}
              label="Type"
              name="type"
              placeholder="Select type"
              {...comboBoxOptionsOf(announcementTypeOptions)}
              required
            />
          </div>
        );
      }}
    </FormModal>
  );
};
